/**
 * CuratedPrint — print-optimized version of a curated guide.
 *
 * This page is NOT part of the authenticated shell. It loads a guide by slug
 * from /api/curated/{slug} (which is public) and renders an editorial layout
 * tuned for letter-size paper. Werner opens it from the CuratedEditor via a
 * "Download PDF" button, which opens this route in a new tab and triggers
 * window.print(). The browser's Save-as-PDF dialog produces the file.
 *
 * Why a dedicated route instead of @media print on CapeTownPublic:
 *   • Print-specific typography and spacing without polluting the web view.
 *   • Explicit page-break rules so sections stay together.
 *   • Removes all chrome (no nav, no credit bar, no cursor-visible affordances).
 *   • Auto-triggers window.print() on load so the UX is one click.
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';

const PRINT_STYLES = `
  @page {
    size: letter;
    margin: 0.6in 0.75in;
  }

  html, body {
    background: #ffffff !important;
    color: #1a1a1a !important;
    font-family: 'Cormorant Garamond', Georgia, serif;
    -webkit-font-smoothing: antialiased;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .print-root {
    max-width: 7in;
    margin: 0 auto;
    padding: 0;
    color: #1a1a1a;
  }

  .print-hero-img {
    display: block;
    width: 100%;
    height: auto;
    max-height: 5.2in;
    object-fit: contain;
    background: #fdfbf4;
    margin-bottom: 0.35in;
    break-after: avoid-page;
  }

  .print-hero-fallback {
    width: 100%;
    height: 0.1in;
    background: #c9a84c;
    margin-bottom: 0.4in;
    break-after: avoid-page;
  }

  .print-section-photo {
    display: block;
    width: 100%;
    height: auto;
    max-height: 4.2in;
    object-fit: contain;
    background: #fdfbf4;
    margin: 0 0 0.2in 0;
    break-inside: avoid;
    break-after: avoid;
  }

  .print-entry-photo {
    display: block;
    width: 100%;
    height: auto;
    max-height: 3.6in;
    object-fit: contain;
    background: #fdfbf4;
    margin: 0.08in 0 0.1in 0;
    break-inside: avoid;
  }

  .print-title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 48pt;
    font-weight: 300;
    line-height: 1.05;
    margin: 0 0 0.12in 0;
    color: #1a1a1a;
    break-after: avoid-page;
  }

  .print-subtitle {
    font-family: 'DM Mono', 'Courier New', monospace;
    font-size: 9pt;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #7a6b3f;
    margin: 0 0 0.35in 0;
    break-after: avoid-page;
  }

  .print-intro {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 14pt;
    line-height: 1.65;
    font-style: italic;
    color: #3a3a3a;
    padding: 0.25in 0;
    border-top: 1px solid #d4cfc2;
    border-bottom: 1px solid #d4cfc2;
    margin-bottom: 0.5in;
    break-after: avoid-page;
    break-inside: avoid;
  }

  .print-section {
    break-inside: avoid-page;
    break-before: auto;
    margin-bottom: 0.45in;
    padding-top: 0.15in;
    border-top: 2px solid #c9a84c;
  }

  .print-section-header {
    display: flex;
    align-items: baseline;
    gap: 0.18in;
    margin-bottom: 0.22in;
    break-after: avoid;
  }

  .print-section-title {
    font-family: 'DM Mono', 'Courier New', monospace;
    font-size: 10pt;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: #c9a84c;
    margin: 0;
    white-space: nowrap;
  }

  .print-section-rule {
    flex: 1;
    height: 0.5pt;
    background: #e0d7b8;
  }

  .print-entry {
    break-inside: avoid;
    padding: 0.13in 0;
    border-bottom: 0.5pt solid #e8e3d9;
  }

  .print-entry:last-child {
    border-bottom: none;
  }

  .print-entry-name {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 17pt;
    font-weight: 400;
    line-height: 1.2;
    color: #1a1a1a;
    margin: 0 0 0.06in 0;
  }

  .print-entry-note {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 11.5pt;
    font-style: italic;
    line-height: 1.55;
    color: #3a3a3a;
    margin: 0 0 0.06in 0;
  }

  .print-entry-url {
    font-family: 'DM Mono', 'Courier New', monospace;
    font-size: 8pt;
    color: #7a6b3f;
    letter-spacing: 0.04em;
    word-break: break-all;
  }

  .print-entry-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.05in;
    margin-top: 0.07in;
  }

  .print-tag {
    font-family: 'DM Mono', 'Courier New', monospace;
    font-size: 7.5pt;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #7a6b3f;
    border: 0.5pt solid #c9a84c;
    padding: 0.02in 0.1in;
  }

  .print-footer {
    margin-top: 0.5in;
    padding-top: 0.25in;
    border-top: 0.5pt solid #d4cfc2;
    font-family: 'DM Mono', 'Courier New', monospace;
    font-size: 8pt;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #7a6b3f;
    text-align: center;
  }

  /* Screen preview — mimic the printed page so Werner sees what he's getting */
  @media screen {
    body { background: #e8e3d9; padding: 0.5in 0; }
    .print-root {
      background: #fdfbf4;
      padding: 0.6in 0.75in;
      box-shadow: 0 8px 40px rgba(0,0,0,0.12);
      min-height: 11in;
      width: 8.5in;
      max-width: none;
    }
    .print-instruction-bar {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: #1a1a1a;
      color: #f0f0f5;
      padding: 10px 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 16px;
      font-family: 'DM Mono', monospace;
      font-size: 11px;
      letter-spacing: 0.08em;
      z-index: 100;
    }
    .print-instruction-bar button {
      background: #c9a84c;
      color: #1a1a1a;
      border: none;
      padding: 6px 16px;
      cursor: pointer;
      font-family: inherit;
      font-size: 10px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
    }
    body { padding-top: 60px !important; }
  }

  @media print {
    .print-instruction-bar { display: none !important; }
    body { padding: 0 !important; background: #ffffff !important; }
    .print-root { box-shadow: none !important; padding: 0 !important; width: auto !important; background: #ffffff !important; }
  }
`;

export default function CuratedPrint() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Load the editorial fonts the print layout depends on.
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=DM+Mono:wght@300;400&display=swap';
    document.head.appendChild(link);

    api.get(`/curated/${slug}`)
      .then(r => {
        setGuide(r.data);
        document.title = `${r.data.title} — Print`;
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));

    return () => {
      if (link.parentNode) document.head.removeChild(link);
    };
  }, [slug]);

  // Auto-trigger print dialog once fonts + images have had a moment to load.
  useEffect(() => {
    if (!guide) return;
    const t = setTimeout(() => {
      // Only auto-print if the user opened this in a fresh tab; if they're
      // just previewing in the same tab, the button in the instruction bar
      // still works.
      if (window.location.search.includes('auto=1')) {
        window.print();
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [guide]);

  if (loading) {
    return <div style={{ padding: 40, fontFamily: 'Cormorant Garamond, serif', color: '#3a3a3a' }}>Loading…</div>;
  }

  if (error || !guide) {
    return <div style={{ padding: 40, fontFamily: 'Cormorant Garamond, serif', color: '#3a3a3a' }}>Guide not found.</div>;
  }

  const sections = [...(guide.sections || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <>
      <style>{PRINT_STYLES}</style>

      <div className="print-instruction-bar">
        <span>Preview of printable guide. Use File → Print → Save as PDF (or Cmd+P) to export.</span>
        <button onClick={() => window.print()}>Print / save as PDF</button>
      </div>

      <div className="print-root">
        {guide.hero_image_url ? (
          <img className="print-hero-img" src={guide.hero_image_url} alt="" />
        ) : (
          <div className="print-hero-fallback" />
        )}

        <h1 className="print-title">{guide.title}</h1>
        {guide.subtitle && <p className="print-subtitle">{guide.subtitle}</p>}

        {guide.intro && <p className="print-intro">{guide.intro}</p>}

        {sections.map(section => {
          const entries = [...(section.entries || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
          return (
            <div className="print-section" key={section.id}>
              {section.photo_url && (
                <img className="print-section-photo" src={section.photo_url} alt="" />
              )}
              <div className="print-section-header">
                <h2 className="print-section-title">{section.title}</h2>
                <div className="print-section-rule" />
              </div>
              {entries.map(entry => (
                <div className="print-entry" key={entry.id}>
                  <p className="print-entry-name">{entry.name}</p>
                  {entry.image_url && (
                    <img className="print-entry-photo" src={entry.image_url} alt="" />
                  )}
                  {entry.note && <p className="print-entry-note">{entry.note}</p>}
                  {entry.url && <p className="print-entry-url">{entry.url}</p>}
                  {Array.isArray(entry.tags) && entry.tags.length > 0 && (
                    <div className="print-entry-tags">
                      {entry.tags.map(t => <span className="print-tag" key={t}>{t}</span>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}

        <div className="print-footer">
          Curated by Werner du Toit · {new Date().getFullYear()} Edition · A Zephyr OS production
        </div>
      </div>
    </>
  );
}
